import { useQuery } from '@tanstack/react-query'
import apiClient from '@/lib/api/client'
import type { ApiResponse } from '@/lib/api/types'
import type { CareCalcReductionType } from './reduction'

/** 공단 인정조사 항목 */
export interface NhisItem {
  code: string
  /** 공단 조사표상 항목명 */
  officialName: string
  /** 보호자에게 묻는 문장 */
  question: string
  /** THREE_LEVEL | MOTOR | JOINT | YES_NO */
  scale: string
  help: string
  /** 점수 오름차순 선택지 라벨 */
  options: string[]
}

/** 공단 5개 영역 */
export interface NhisArea {
  area: string
  /** 공단 공식 영역명 (신체기능 등) */
  officialLabel: string
  /** 보호자용 영역명 */
  userLabel: string
  itemCount: number
  items: NhisItem[]
}

/** 점수에 반영되지 않는 별도 문항 (치매 진단·감경) */
export interface ExtraQuestion {
  code: string
  question: string
  help: string
  options: { value: string; label: string }[]
}

export interface QuestionsResponse {
  rulesetVersion: string
  /** QUICK | FULL */
  mode: string
  totalItems: number
  unknownValue: string
  source: string
  areas: NhisArea[]
  extraQuestions: ExtraQuestion[]
}

/** 재가/시설 각각의 금액 블록 */
export interface CostBlock {
  available: boolean
  serviceType: string | null
  copayLow: number
  copayHigh: number
  nonCoveredLow: number
  nonCoveredHigh: number
  /** FACILITY | REGION_AVG | TYPE_DEFAULT */
  nonCoveredSource: string | null
  totalLow: number
  totalHigh: number
  unavailableReason: string | null
}

/** 지원 항목 */
export interface BenefitItemView {
  code: string
  title: string
  summary: string
  amount: string | null
  /** CALCULATED | FACILITY_ACTUAL | APPROX | UNKNOWN */
  tier: string
  tierIcon: string
  note: string | null
}

/** 영역별 지원 묶음 */
export interface BenefitGroup {
  domain: string
  domainLabel: string
  items: BenefitItemView[]
}

export interface EstimateResponse {
  rulesetVersion: string
  rateYear: number
  /** A~E | NONE | INSUFFICIENT */
  band: string
  bandLabel: string
  bandDescription: string
  borderline: boolean
  answeredCount: number
  unknownCount: number
  domainScores: Record<string, number>
  home: CostBlock
  facility: CostBlock
  homeCareNote: string | null
  disclaimer: string
  confirmedFacts: string[]
  benefitGroups: BenefitGroup[]
  benefitCount: number
}

/** 금액 신뢰도 4단계 설명 — 정확도가 다른 것을 같게 보여주면 전부 못 믿게 된다 */
export const TIER_LABEL: Record<string, string> = {
  CALCULATED: '공단이 정한 금액이에요',
  FACILITY_ACTUAL: '시설이 신고한 실제 금액이에요',
  APPROX: '금액은 알지만 대상이 되시는지는 확인이 필요해요',
  UNKNOWN: '사시는 지역에 따라 달라요',
}

export interface EstimateRequest {
  rulesetVersion: string
  answers: Record<string, string>
  mode?: 'QUICK' | 'FULL'
  /** 위치(선택). 서버가 시·군·구로 바꿔 지역 평균 비급여에 쓴다 */
  lat?: number
  lng?: number
  utilizationRate?: number
}

/** 문항 목록 조회 — 비인증 공개 */
export function useCalcQuestions(mode: 'QUICK' | 'FULL' = 'FULL') {
  return useQuery({
    queryKey: ['care-calculator', 'questions', mode],
    queryFn: async () => {
      const res = await apiClient.get<ApiResponse<QuestionsResponse>>(
        '/care-calculator/questions',
        { params: { mode } },
      )
      return res.data.data
    },
    staleTime: 60 * 60 * 1000, // 문항은 배포 단위로만 바뀐다
  })
}

/** 답변 → 밴드 + 금액 구간. 저장하지 않는다 */
export async function estimateCareBenefit(
  request: EstimateRequest,
): Promise<EstimateResponse> {
  const res = await apiClient.post<ApiResponse<EstimateResponse>>(
    '/care-calculator/estimate',
    request,
  )
  return res.data.data
}

/** 만원 단위 표기. 40~60대 대상이라 "1,528,200원"보다 "약 153만원"이 읽기 쉽다 */
export function formatManWon(amount: number): string {
  return `${Math.round(amount / 10000).toLocaleString()}만원`
}

/** 금액 구간 표기. 상·하한이 같으면 단일 표기 */
export function formatRange(low: number, high: number): string {
  if (low === high) return `약 ${formatManWon(low)}`
  return `약 ${formatManWon(low)} ~ ${formatManWon(high)}`
}

/** 예산으로 찾은 시설 */
export interface BudgetFacility {
  facilityId: number
  name: string
  address: string
  evaluationGrade: string | null
  capacity: number | null
  currentCount: number | null
  hasVacancy: boolean
  nonCoveredMonthly: number
  totalMonthly: number
  /** 비급여 신고 자료가 있는 시설인지. 없으면 총액이 낮게 보일 수 있다 */
  hasNonCoveredData: boolean
  distanceKm: number
}

export interface BudgetFacilityResponse {
  careGrade: string
  facilityType: string
  copayMonthly: number
  budgetMax: number
  /** 반경 안의 같은 유형 시설 수 (예산 조건 제외) */
  nearbyCount: number
  matchedCount: number
  facilities: BudgetFacility[]
}

/**
 * 계산된 예산으로 갈 수 있는 시설 조회.
 *
 * 기존 시설 검색은 예산 파라미터를 받아도 배너만 띄우고 필터링하지 않으므로
 * 전용 조회를 쓴다.
 *
 * `reductionType` 은 필수다. 빠지면 서버가 일반(20%)으로 걸러 감경 예산과
 * 일반 금액을 비교하게 된다 (영구지침 carecalc §3-4-1).
 */
export async function findFacilitiesWithinBudget(params: {
  band: string
  budgetMax: number
  lat: number
  lng: number
  reductionType: CareCalcReductionType
  radiusKm?: number
  facilityType?: string
}): Promise<BudgetFacilityResponse> {
  const res = await apiClient.get<ApiResponse<BudgetFacilityResponse>>(
    '/care-calculator/facilities',
    { params: { radiusKm: 10, facilityType: 'NURSING_HOME', ...params } },
  )
  return res.data.data
}
